/**
 * `grid telemetry list` — terminal access to the cli-events stream
 * (backlog 095). Mirrors what Console renders at `/admin/cli-events` and
 * `/account/cli-events` (decision 016 + kickoff 12). The CLI passes
 * filters through to `GET /api/v2/telemetry/cli-events`; the server
 * gates self-scope vs cluster-wide based on the caller's admin role
 * (CLI does NO RBAC of its own).
 *
 * Aliases: `grid feedback list` and `grid errors list` are
 * thin wrappers that pre-set `--type` and call the same handler.
 */

import { Command } from 'commander';
import type { IV2Client } from '../api/v2-client.js';
import { getV2Client } from '../api/v2-client.js';
import { CloudGridError } from '../utils.js';
import { handleV2Error } from '../handle-error.js';
import { addGridOption, gridSlug, type GridOpts } from '../grid-opt.js';

const VALID_TYPES = ['error', 'success', 'feedback'] as const;
type EventTypeFilter = (typeof VALID_TYPES)[number];

const DEFAULT_LIMIT = 50;
const MAX_LIMIT = 200;

export interface TelemetryListOpts extends GridOpts {
  type?: string;
  userEmail?: string;
  since?: string;
  limit?: string | number;
  json?: boolean;
}

export interface TelemetryListDeps {
  client?: IV2Client;
}

interface CliEventRow {
  id?: string;
  user_id?: string;
  user_email?: string;
  org_slug?: string | null;
  entity_id?: string | null;
  event_type?: string;
  command?: string;
  error_code?: string | null;
  error_status?: number | null;
  text?: string | null;
  log_excerpt?: string | null;
  elapsed_ms?: number | null;
  created_at?: string;
}

interface CliEventsResponse {
  events?: CliEventRow[];
  next_cursor?: string;
}

export async function runTelemetryList(
  opts: TelemetryListOpts,
  deps: TelemetryListDeps = {},
): Promise<void> {
  const eventType = parseEventType(opts.type);
  const limit = parseLimit(opts.limit);
  const since = parseSince(opts.since);

  const qs: Record<string, string | number | undefined> = {
    limit,
  };
  if (eventType) qs.event_type = eventType;
  const grid = gridSlug(opts);
  if (grid) qs.org_slug = grid;
  if (opts.userEmail) qs.user_email = opts.userEmail;
  if (since) qs.since = since;

  const client = deps.client ?? (await getV2Client());
  const resp = await client.get<CliEventsResponse>(
    '/api/v2/telemetry/cli-events',
    qs,
  );

  if (opts.json) {
    process.stdout.write(JSON.stringify(resp ?? { events: [] }, null, 2) + '\n');
    return;
  }

  const events = Array.isArray(resp?.events) ? resp!.events! : [];
  if (events.length === 0) {
    process.stdout.write('No events.\n');
    return;
  }

  for (const ev of events) {
    process.stdout.write(formatRow(ev) + '\n');
  }
}

function parseEventType(raw: string | undefined): EventTypeFilter | undefined {
  if (raw === undefined || raw === '') return undefined;
  if (!(VALID_TYPES as readonly string[]).includes(raw)) {
    throw new CloudGridError(
      `Invalid --type '${raw}'. Choose one of: ${VALID_TYPES.join(', ')}.`,
    );
  }
  return raw as EventTypeFilter;
}

function parseLimit(raw: string | number | undefined): number {
  if (raw === undefined || raw === '') return DEFAULT_LIMIT;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) {
    throw new CloudGridError(`Invalid --limit '${raw}'. Must be a positive integer.`);
  }
  const clamped = Math.min(MAX_LIMIT, Math.floor(n));
  return clamped;
}

/**
 * Accept either an absolute ISO 8601 timestamp or a relative duration
 * like `24h`, `30m`, `7d`. Returns an ISO 8601 string (UTC) suitable
 * for the server's `since` filter.
 */
function parseSince(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const m = /^(\d+)\s*([smhd])$/.exec(raw.trim());
  if (m) {
    const n = Number(m[1]);
    const unit = m[2];
    const seconds = unit === 's' ? n : unit === 'm' ? n * 60 : unit === 'h' ? n * 3600 : n * 86400;
    return new Date(Date.now() - seconds * 1000).toISOString();
  }
  // Try parsing as an absolute date/time.
  const ms = Date.parse(raw);
  if (!Number.isNaN(ms)) return new Date(ms).toISOString();
  throw new CloudGridError(
    `Invalid --since '${raw}'. Use ISO 8601 (e.g. 2026-05-01T00:00:00Z) or a relative duration (e.g. 24h, 7d, 30m).`,
  );
}

/**
 * Render one event as two lines: a header (timestamp, user, org, type,
 * command/error_code) and an indented body (`text`, `error_code` detail,
 * or elapsed_ms). Server response carries `user_id` rather than email
 * today; render whichever the API gives us.
 */
function formatRow(ev: CliEventRow): string {
  const ts = formatTimestamp(ev.created_at);
  const user = ev.user_email || ev.user_id || '—';
  const org = ev.org_slug || '—';
  const type = (ev.event_type || 'unknown').toUpperCase();

  const headerParts = [ts, user, org, type];
  if (ev.event_type === 'error' || ev.event_type === 'success') {
    if (ev.command) headerParts.push(ev.command);
  }
  let line = headerParts.join('  ');

  // Body line — skip blank events.
  const body = ev.text
    || (ev.error_code ? `${ev.error_code}${ev.error_status ? ` (${ev.error_status})` : ''}` : null)
    || (typeof ev.elapsed_ms === 'number' ? `${ev.elapsed_ms} ms` : null);
  if (body) {
    line += `\n  ${body}`;
  }
  return line;
}

function formatTimestamp(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    // YYYY-MM-DD HH:MM (UTC). The CLI stays terse; users who need
    // local time can pipe through --json into their tool of choice.
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  } catch {
    return iso;
  }
}

// ── Commander binding ────────────────────────────────────────────────────────

/**
 * Build the `telemetry` command tree. A factory (not a singleton) so it can
 * mount under both the top-level program (deprecated, BL598 Phase 1A) and the
 * `admin` parent (canonical) without a commander parent collision.
 */
export function makeTelemetryCommand(): Command {
  const telemetryCommand = new Command('telemetry')
    .description('Inspect CLI telemetry (backlog 095, decision 016)');

  const listCommand = telemetryCommand
    .command('list')
    .description('List recent CLI telemetry events (admin: cluster-wide; otherwise self-scoped)')
    .option('--type <type>', 'Filter by event type (error | success | feedback)')
    .option('--user-email <email>', 'Filter by user email (admin only — server-gated)');
  addGridOption(listCommand, 'Filter by grid slug');
  listCommand
    .option('--since <duration>', 'Restrict to events newer than this (e.g. 24h, 7d, 30m, ISO 8601)')
    .option('--limit <n>', `Number of events to return (default ${DEFAULT_LIMIT}, max ${MAX_LIMIT})`)
    .option('--json', 'Emit the API response shape directly')
    .action(async (opts: TelemetryListOpts) => {
      try {
        await runTelemetryList(opts);
      } catch (err) {
        await handleV2Error(err, { json: Boolean(opts.json) });
      }
    });

  return telemetryCommand;
}

export const telemetryCommand = makeTelemetryCommand();

// ── Aliases (stretch in the spec — small wrappers) ──────────────────────────
// `grid feedback list` is wired by adding a subcommand to the existing
// `feedbackCommand` in feedback.ts. `grid errors list` is a fresh
// top-level verb defined here; it has no peer "send" form.

/**
 * Build the `errors` command tree (alias of `telemetry list --type error`).
 * Factory for the same dual-mount reason as `makeTelemetryCommand`.
 */
export function makeErrorsCommand(): Command {
  const errorsCommand = new Command('errors')
    .description('Inspect CLI error events (alias of `grid telemetry list --type error`)');

  const errListCommand = errorsCommand
    .command('list')
    .description('List error events (alias of `grid telemetry list --type error`)')
    .option('--user-email <email>', 'Filter by user email (admin only)');
  addGridOption(errListCommand, 'Filter by grid slug');
  errListCommand
    .option('--since <duration>', 'Restrict to events newer than this (e.g. 24h, 7d)')
    .option('--limit <n>', `Number of events to return (default ${DEFAULT_LIMIT}, max ${MAX_LIMIT})`)
    .option('--json', 'Emit the API response shape directly')
    .action(async (opts: TelemetryListOpts) => {
      try {
        await runTelemetryList({ ...opts, type: 'error' });
      } catch (err) {
        await handleV2Error(err, { json: Boolean(opts.json) });
      }
    });

  return errorsCommand;
}

export const errorsCommand = makeErrorsCommand();
